<script>
import ResultDisplay from '@homepage/components/pageStyles/siprizLotto/ResultDisplay.vue';
import Countdown from '@homepage/components/Countdown.vue';
import { generateImageMapFor } from '@homepage/helpers/image.js';
import PrizesList from '@homepage/components/PrizesList.vue';
import CardPresenter from '@homepage/components/CardPresenter.vue';
import LotteryResultAutoDisplay from '@homepage/components/LotteryResultAutoDisplay.vue';
import LotteryResult from '@homepage/components/LotteryResult.vue';

export default {
	name: 'SiprizLottoBoard',
	components: { CardPresenter, Countdown, ResultDisplay, PrizesList, LotteryResultAutoDisplay, LotteryResult },
	props: {
		generalInformation: {
			type: Object,
			required: true
		}
	},
	provide() {
		return {
			imagesMap: generateImageMapFor('quickmoney'),
			generalInformation: this.generalInformation
		};
	},
	data() {
		return {
			timeToShowPrizesPage: 8000,
			timeToShowEachLotteryResultPage: 8000,
			lotteryEntriesPerPage: 2
		};
	},
	beforeCreate() {
		this.$emit('change-style', { backgroundImage: require('@/assets/img/siprizlotto/background.jpg') });
	},
	computed: {
		timeToShowResultsPage() {
			const timeToShowEachPage = this.timeToShowEachLotteryResultPage / 1000;

			return Math.ceil(this.generalInformation.lotteriesResultsEntries.length / this.lotteryEntriesPerPage) * timeToShowEachPage * 1000;
		}
	}
};
</script>

<template>
	<v-row justify='center'>
		<v-col cols='12' class='py-0'>
			<v-row justify='center' align='stretch'>
				<v-col cols='4' class='d-flex flex-column'>
					<card-presenter
						:time-per-page='[timeToShowPrizesPage, timeToShowResultsPage]'>
						<template #page-1>
							<div class='card-header'>PAYOUTS</div>
							<v-card class='secondary-card' shaped>
								<prizes-list :general-information='generalInformation' class='px-3 py-1' :rounded='false'>
									<template #prize-row='{prize}'>
										<v-col class='prize-row' cols='12'>
											<v-row no-gutters class='py-4'>
												<v-col class='prize-name text-center' cols='6'>
													{{ prize.name }} - 1X:
												</v-col>
												<v-col class='prize-amount' cols='6'>
													{{ prize.amount }}
												</v-col>
											</v-row>
										</v-col>
									</template>
								</prizes-list>
							</v-card>
						</template>
						<template #page-2>
							<div class='card-header'>RESULTS</div>
							<v-card id='lottery-results' class='secondary-card' shaped>
								<lottery-result-auto-display
									:general-information='generalInformation'
									:entries-per-page='lotteryEntriesPerPage'
									:time-to-show-each-page='timeToShowEachLotteryResultPage'>
									<template #page='{ entries }'>
										<v-container class='px-0 py-0'>
											<lottery-result v-for='(entry, index) in entries'
																			class='lottery-result'
																			:class="{'rounded-b-xl': index === entries.length - 1}"
																			:show-pick-five='true'
																			:key='entry.name'
																			:lottery='entry'>
												<template #sortition-header='{sortition, icon, date}'>
															<span class='sortition-header'>
																<font-awesome-icon :icon='icon'></font-awesome-icon>
																{{ sortition.name }}: {{ date }}
															</span>
												</template>
											</lottery-result>
										</v-container>
									</template>
								</lottery-result-auto-display>
							</v-card>
						</template>
					</card-presenter>
				</v-col>
				<v-col cols='4'>
					<v-img height='134' contain :src="require('@/assets/img/siprizlotto/logo.png')"></v-img>
					<v-row>
						<v-col cols='12'>
							<countdown
								id='upcoming-result'
								:sortition='generalInformation.sortition'
								class='text-center'>
								<template #default='{sortition}'>
									<v-flex md3>
										<h1 class='font-weight-bold text-uppercase'>Next drawing in</h1>
									</v-flex>
									<v-flex md3>
										<h1 id='next-sortition' class='font-weight-bold'>{{ sortition.name }}</h1>
									</v-flex>
									<v-flex md3>
										<span id='time-left' v-if='sortition.timeLeft'
													class='font-weight-black text-uppercase'>{{ sortition.timeLeft }}</span>
										<span v-else><font-awesome-icon icon='sync' spin size='3x'></font-awesome-icon></span>
									</v-flex>
								</template>
							</countdown>
						</v-col>
					</v-row>
					<v-card
						v-if='generalInformation.results.slice(0, 1).length > 0'
						id='primary-card'
						shaped
					>
						<v-card-text>
							<h1 id='winning-numbers-text' class='text-center my-1'>
								Winning numbers
							</h1>
							<result-display
								v-for='result in generalInformation.results.slice(0, 1)'
								:key='result.sortition.id'
								:result='result'
							>
							</result-display>
						</v-card-text>
					</v-card>
				</v-col>
				<v-col
					cols='4'
					v-if='generalInformation.results.slice(1,3).length > 0'
				>
					<div class='card-header'><span>Pick</span> <span>Numbers</span></div>
					<v-card shaped class='secondary-card'>
						<v-card-text>
							<result-display
								v-for='result in generalInformation.results.slice(1,3)'
								:key='result.sortition.id'
								:result='result'
							>
							</result-display>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-col>
		<v-col id='page-url' cols='12' class='text-center px-0 py-0'>
			<span>Visit our page siprizlotto.net</span>
		</v-col>
	</v-row>
</template>

<style scoped lang='scss'>

@font-face {
	font-family: "Made Tommy Outline";
	src: url("../../../../public/fonts/MADE TOMMY Bold Outline.otf");
}

@font-face {
	font-family: "Made Tommy Bold";
	src: url("../../../../public/fonts/MADE TOMMY ExtraBold.otf");
}

$card-background: rgba(#2b55bc, 0.5);

* {
	color: white;
	font-family: "Made Tommy Bold", "Liberation Sans Narrow", serif;
}

#primary-card {
	background-color: transparent;
	border: 2px solid white;
}

.v-card.secondary-card {
	background-color: darken($card-background, 20);
	box-shadow: 8px 6px 20px 0 #07000080;
}

#time-left {
	font-weight: bold;
	font-size: 2.4rem;
	text-align: center;
	color: #c11d1e;
}

.card-header {
	box-shadow: -3px 3px 7px 0px #0000009e;
	font-size: 1.5rem;
	padding: 0 6px;
	margin-bottom: 5px;
	margin-left: 10px;
	margin-right: 10px;
	display: flex;
	flex-direction: row;
	justify-content: space-around;
	border-radius: 30px;
	background-image: linear-gradient(to right, #fd383b, #c11d1e);
}

#page-url {
	font-size: 1.5rem;
}

.prize-row {
	font-family: "Intro", serif;
	font-size: 1.7rem;
	line-height: 1.7rem;
	border: solid red 2px;
	border-radius: 15px;
	padding: 10px;
}

::v-deep .sortition-result {
	padding: 8px 0;
}

::v-deep .sortition-result:not(:last-child) {
	border-bottom: none;
}

::v-deep .sortition-result:first-child {
	padding-top: 12px;
}

::v-deep .sortition-result:last-child {
	padding-bottom: 12px;
}
</style>
